import React from "react";
import { Link } from "react-router-dom";
import {
  Printer, LogIn, ShieldCheck, UserRound, Users, Command, Bell,
  CalendarClock, Mail, Sparkles, KeyRound, Smartphone,
} from "lucide-react";

const GROUPS = [
  {
    icon: KeyRound, title: "Sign-In & Access",
    items: [
      "Sign in with Google or with email + password (register, forgot & reset password).",
      "Role-based access for Recruits, Recruiters/Cadre, Admins, and Family.",
      "Admin approval gate with title exclusivity (one Commander, one 1SG, etc.).",
      "Password-protected dev login for testing, disabled on the live site.",
    ],
  },
  {
    icon: UserRound, title: "Recruit Experience",
    items: [
      "Personal dashboard with an automatic ship-day countdown and a \u201cHas Shipped\u201d status.",
      "Editable profile: ship date, BCT location, MOS, rank, phone, contact email, CAC, photo.",
      "View the training schedule, leadership directory, resources, and MOS information.",
      "One-tap call or email your recruiter.",
    ],
  },
  {
    icon: Users, title: "Recruiter / Cadre Tools",
    items: [
      "Manage a personal roster with notes, editing, and status tracking.",
      "AI roster import \u2014 upload a PDF/Excel/CSV to bulk-add recruits.",
      "Email All / Text All your not-yet-shipped recruits in one tap.",
      "Broadcast messages, a secondary contact email, and automatic note alerts.",
    ],
  },
  {
    icon: Command, title: "Admin / Command Center",
    items: [
      "Command dashboard: recruit stats, gender breakdown, recruiter filter, Email/Text All.",
      "Create a profile for any role, edit any recruit, and manage all content.",
      "AI Training-Schedule Import from a document or photo, with a confirm-and-edit preview.",
      "Account tools: reset links, re-send welcome email, and auto-purge shipped recruits.",
      "\u201cView As\u201d any role with a persistent Return-to-Admin banner.",
    ],
  },
  {
    icon: CalendarClock, title: "Training & Schedule",
    items: [
      "Battle-rhythm training list with single and multi-day events (DD-DD MMM YYYY).",
      "Admins add events manually or import an entire schedule with AI.",
      "Ship-off celebration banner and automatic archiving of shipped recruits.",
    ],
  },
  {
    icon: Smartphone, title: "Platform & Notifications",
    items: [
      "Installable Progressive Web App (PWA) that works app-like on any phone.",
      "Web push notifications and automated ship-day reminders.",
      "Branded transactional emails (welcome, resets, note alerts) with the RSP emblem.",
      "Tactical dark theme, mobile-first and fully responsive.",
    ],
  },
];

function FeatureGroup({ icon: Icon, title, items }) {
  return (
    <div className="rsp-card p-5 break-inside-avoid">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--rsp-bg-2)] border border-[var(--rsp-border)] flex items-center justify-center">
          <Icon className="gold" size={18} />
        </div>
        <h2 className="font-head uppercase tracking-wide text-lg leading-none">{title}</h2>
      </div>
      <ul className="space-y-2">
        {items.map((t) => (
          <li key={t} className="flex gap-2.5 text-sm text-[var(--rsp-gray)] leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--rsp-gold)] shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Overview() {
  return (
    <div className="min-h-screen bg-[var(--rsp-bg)] rsp-scroll overflow-y-auto" data-testid="overview-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #09090b !important; }
          @page { margin: 12mm; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-10 no-print">
          <div className="flex items-center gap-3">
            <img src="/emblem.png" alt="RSP Riverside emblem" className="w-11 h-11 object-contain" />
            <span className="font-head uppercase tracking-wider text-sm gold">RSP Riverside</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="overview-print-btn"
              className="pill" title="Print or save as PDF">
              <Printer size={14} /> Print / PDF
            </button>
            <Link to="/login" data-testid="overview-signin-btn" className="btn-gold px-4 py-2 flex items-center gap-1.5 text-sm">
              <LogIn size={15} /> Sign In
            </Link>
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-12">
          <img src="/emblem.png" alt="RSP Riverside emblem" className="w-24 h-24 object-contain mx-auto mb-5" />
          <div className="eyebrow mb-2" style={{ letterSpacing: "0.22em" }}>Recruit Sustainment Program</div>
          <h1 className="heading text-4xl sm:text-5xl lg:text-6xl mb-4">RSP Riverside</h1>
          <p className="text-[var(--rsp-gray)] max-w-2xl mx-auto leading-relaxed">
            The all-in-one command app that keeps recruits ship-ready and connects them with their
            recruiters and cadre — countdowns, training, leadership, messaging, and more, right on their phone.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[var(--rsp-muted)]">
            <ShieldCheck size={13} className="gold" /> Secure · Role-based · Installable PWA
          </div>
        </div>

        {/* Feature groups */}
        <div className="grid sm:grid-cols-2 gap-4">
          {GROUPS.map((g) => <FeatureGroup key={g.title} {...g} />)}
        </div>

        {/* Highlights strip */}
        <div className="grid grid-cols-3 gap-4 mt-10 text-center">
          {[
            { icon: Sparkles, label: "AI Roster & Schedule Import" },
            { icon: Bell, label: "Web Push & Email Alerts" },
            { icon: Mail, label: "Branded RSP Emails" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="rsp-card p-4 break-inside-avoid">
              <Icon className="gold mx-auto mb-2" size={20} />
              <div className="text-xs text-[var(--rsp-gray)] leading-snug">{label}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="rsp-card p-6 mt-10 text-center no-print">
          <h2 className="font-head uppercase tracking-wide text-2xl mb-2">Ready to fall in?</h2>
          <p className="text-[var(--rsp-gray)] text-sm mb-4">Sign in with the email your recruiter set up for you.</p>
          <Link to="/login" className="btn-gold inline-flex px-6 py-2.5 items-center gap-2">
            <LogIn size={16} /> Sign In to RSP Riverside
          </Link>
        </div>

        <div className="text-center text-[var(--rsp-muted)] text-xs mt-10">
          RSP Riverside — rspriverside.com · <Link to="/privacy" className="underline hover:text-[var(--rsp-gray)]">Privacy</Link>
        </div>
      </div>
    </div>
  );
}
